import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface AuthBackupDB extends DBSchema {
  sessions: {
    key: string;
    value: {
      key: string;
      value: string;
      timestamp: number;
    };
  };
}

class StorageBackup {
  private dbPromise: Promise<IDBPDatabase<AuthBackupDB>> | null = null;
  private memoryBackup: Map<string, string> = new Map();
  private dbInitializing = false;

  private async getDB(): Promise<IDBPDatabase<AuthBackupDB> | null> {
    if (this.dbInitializing && this.dbPromise) {
      try {
        return await this.dbPromise;
      } catch {
        // Failed, will retry below
      }
    }

    if (this.dbPromise) {
      try {
        const db = await this.dbPromise;
        await db.get('sessions', 'test');
        return db;
      } catch {
        this.dbPromise = null;
      }
    }

    if (!this.dbInitializing) {
      this.dbInitializing = true;
      this.dbPromise = openDB<AuthBackupDB>('auth-backup', 1, {
        upgrade(db: IDBPDatabase<AuthBackupDB>) {
          if (!db.objectStoreNames.contains('sessions')) {
            db.createObjectStore('sessions', { keyPath: 'key' });
          }
        },
      }).then(db => {
        this.dbInitializing = false;
        return db;
      }).catch((err: Error) => {
        console.error('Failed to open IndexedDB:', err);
        this.dbInitializing = false;
        this.dbPromise = null;
        return null as any;
      });
    }

    try {
      return await this.dbPromise;
    } catch {
      return null;
    }
  }

  async backup(key: string, value: string): Promise<void> {
    this.memoryBackup.set(key, value);
    console.log(`💾 Backed up ${key} to memory`);

    try {
      sessionStorage.setItem(key, value);
      console.log(`💾 Backed up ${key} to sessionStorage`);
    } catch (e) {
      console.warn('SessionStorage backup failed:', e);
    }

    try {
      const db = await this.getDB();
      if (db) {
        await db.put('sessions', {
          key,
          value,
          timestamp: Date.now()
        });
        console.log(`💾 Backed up ${key} to IndexedDB`);
      }
    } catch (e) {
      console.warn('IndexedDB backup failed:', e);
    }
  }

  async restore(key: string): Promise<string | null> {
    const memoryValue = this.memoryBackup.get(key);
    if (memoryValue) {
      console.log(`✅ Restored ${key} from memory backup`);
      return memoryValue;
    }

    try {
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue) {
        console.log(`✅ Restored ${key} from sessionStorage backup`);
        this.memoryBackup.set(key, sessionValue); // Re-populate memory
        return sessionValue;
      }
    } catch (e) {
      console.warn('SessionStorage restore failed:', e);
    }

    try {
      const db = await this.getDB();
      if (db) {
        const record = await db.get('sessions', key);
        if (record && record.value) {
          console.log(`✅ Restored ${key} from IndexedDB backup`);
          this.memoryBackup.set(key, record.value); // Re-populate memory
          
          try {
            sessionStorage.setItem(key, record.value);
          } catch {}
          
          return record.value;
        }
      }
    } catch (e) {
      console.warn('IndexedDB restore failed:', e);
    }

    return null;
  }

  async clear(key: string): Promise<void> {
    this.memoryBackup.delete(key);

    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.warn('SessionStorage clear failed:', e);
    }

    try {
      const db = await this.getDB();
      if (db) {
        await db.delete('sessions', key);
      }
    } catch (e) {
      console.warn('IndexedDB clear failed:', e);
    }
  }

  async cleanup(): Promise<void> {
    try {
      const db = await this.getDB();
      if (db) {
        const tx = db.transaction('sessions', 'readwrite');
        const store = tx.objectStore('sessions');
        const allKeys = await store.getAllKeys();
        
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        
        for (const key of allKeys) {
          const record = await store.get(key);
          if (record && record.timestamp < sevenDaysAgo) {
            await store.delete(key);
          }
        }
      }
    } catch (e) {
      console.warn('Cleanup failed:', e);
    }
  }
}

export const storageBackup = new StorageBackup(); 
